import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import {
  downloadInvoicePdf,
  ensureInvoiceForProduct,
  InvoiceAvailabilityItem,
  InvoiceSummary,
  listInvoices,
} from "../../util/api/invoiceApi";

const INVOICE_ERROR_MESSAGE = "Invoice unavailable. Please try again.";
const COMPACT_INVOICE_MESSAGE_SX = {
  fontSize: "0.625rem",
  lineHeight: 1.2,
} as const;
const COMPACT_INVOICE_ALERT_SX = {
  mb: 2,
  py: 0,
  px: 1,
  width: "fit-content",
  maxWidth: "100%",
  alignItems: "center",
  "& .MuiAlert-icon": { py: 0.4, mr: 0.75, fontSize: "1rem" },
  "& .MuiAlert-message": { py: 0.4 },
  "& .MuiAlert-action": { py: 0.15, pl: 0.75, mr: 0 },
  "& .MuiButton-root": { fontSize: "0.625rem", lineHeight: 1.2, minWidth: 0 },
} as const;

const setRecordFlag = (
  current: Record<string, boolean>,
  key: string,
  value: boolean
): Record<string, boolean> => ({ ...current, [key]: value });

const formatIssueDate = (issueDate: string): string => {
  const dateParts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(issueDate);

  if (!dateParts) {
    return issueDate;
  }

  const parsedDate = new Date(Date.UTC(
    Number(dateParts[1]),
    Number(dateParts[2]) - 1,
    Number(dateParts[3])
  ));

  if (Number.isNaN(parsedDate.getTime())) {
    return issueDate;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(parsedDate);
};

const invoiceDescription = (invoice: InvoiceSummary): string => {
  const descriptions = invoice.products
    .map((product) => product.description.trim())
    .filter((description) => description.length > 0);

  return descriptions.length > 0 ? descriptions.join(", ") : "WaveMind purchase";
};

interface ProductInvoiceActionProps {
  availability?: InvoiceAvailabilityItem;
  productUuid: string;
  productName?: string;
  onInvoiceIssued?: (invoice: InvoiceSummary) => void;
}

export const ProductInvoiceAction: React.FC<ProductInvoiceActionProps> = ({
  availability,
  productUuid,
  productName,
  onInvoiceIssued,
}) => {
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const requestInFlight = useRef(false);

  const hasIssuedInvoice = Boolean(
    availability?.state === "issued"
    && availability.invoice_uuid
    && availability.invoice_number
  );
  const accessibleName = `${
    hasIssuedInvoice ? "Download" : "Create & download"
  } invoice for ${productName || productUuid}`;

  const handleInvoiceAction = async () => {
    if (requestInFlight.current) {
      return;
    }

    requestInFlight.current = true;
    setLoading(true);
    setHasError(false);

    try {
      let invoiceUuid = availability?.invoice_uuid;
      let invoiceNumber = availability?.invoice_number;

      if (!invoiceUuid || !invoiceNumber) {
        const invoice = await ensureInvoiceForProduct(productUuid);
        invoiceUuid = invoice.invoice_uuid;
        invoiceNumber = invoice.invoice_number;
        onInvoiceIssued?.(invoice);
      }

      if (!invoiceUuid || !invoiceNumber) {
        throw new Error("Invoice metadata is unavailable");
      }

      await downloadInvoicePdf(invoiceUuid, invoiceNumber);
    } catch (_error) {
      setHasError(true);
    } finally {
      requestInFlight.current = false;
      setLoading(false);
    }
  };

  return (
    <Box>
      <Button
        aria-label={accessibleName}
        disabled={loading}
        onClick={handleInvoiceAction}
        startIcon={loading ? <CircularProgress aria-hidden size={16} /> : <ReceiptLongIcon />}
        color="secondary"
        variant="contained"
        sx={{ background: "#fff", width: { xs: "100%", sm: "auto" } }}
      >
        {loading ? (hasIssuedInvoice ? "Downloading…" : "Preparing invoice…") : "Invoice"}
      </Button>
      {hasError && (
        <Typography
          role="alert"
          variant="caption"
          sx={{ color: "error.main", display: "block", mt: 0.75, ...COMPACT_INVOICE_MESSAGE_SX }}
        >
          {INVOICE_ERROR_MESSAGE}
        </Typography>
      )}
    </Box>
  );
};

interface InvoiceAvailabilityStatusProps {
  loading: boolean;
  hasError: boolean;
  legacyChecksDegraded: boolean;
  onRetry: () => void;
}

export const InvoiceAvailabilityStatus: React.FC<InvoiceAvailabilityStatusProps> = ({
  loading,
  hasError,
  legacyChecksDegraded,
  onRetry,
}) => {
  if (loading) {
    return (
      <Box role="status" aria-label="Checking invoice availability" sx={{ display: "flex", gap: 1, mb: 2 }}>
        <CircularProgress aria-hidden size={18} sx={{ color: "black" }} />
        <Typography variant="body2">Checking invoice availability…</Typography>
      </Box>
    );
  }

  if (hasError) {
    return (
      <Alert
        severity="warning"
        sx={COMPACT_INVOICE_ALERT_SX}
        action={
          <Button color="inherit" size="small" onClick={onRetry} aria-label="Retry invoice availability">
            Retry
          </Button>
        }
      >
        <Typography component="span" sx={COMPACT_INVOICE_MESSAGE_SX}>
          Invoice options are temporarily unavailable. Your licenses are unaffected.
        </Typography>
      </Alert>
    );
  }

  if (legacyChecksDegraded) {
    return (
      <Alert severity="info" sx={COMPACT_INVOICE_ALERT_SX}>
        <Typography component="span" sx={COMPACT_INVOICE_MESSAGE_SX}>
          Some older invoice options are temporarily unavailable. Issued invoices remain accessible below.
        </Typography>
      </Alert>
    );
  }

  return null;
};

interface InvoiceOverviewProps {
  refreshKey?: number;
}

export const InvoiceOverview: React.FC<InvoiceOverviewProps> = ({ refreshKey = 0 }) => {
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});
  const [downloadErrors, setDownloadErrors] = useState<Record<string, boolean>>({});
  const downloadRequests = useRef<Record<string, boolean>>({});

  useEffect(() => {
    let active = true;

    setLoading(true);
    setHasError(false);

    listInvoices()
      .then((items) => {
        if (active) {
          setInvoices(items);
        }
      })
      .catch(() => {
        if (active) {
          setHasError(true);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [refreshKey, retryKey]);

  const handleDownload = async (invoice: InvoiceSummary) => {
    const invoiceUuid = invoice.invoice_uuid;

    if (downloadRequests.current[invoiceUuid]) {
      return;
    }

    downloadRequests.current[invoiceUuid] = true;
    setDownloading((current) => setRecordFlag(current, invoiceUuid, true));
    setDownloadErrors((current) => setRecordFlag(current, invoiceUuid, false));

    try {
      await downloadInvoicePdf(invoiceUuid, invoice.invoice_number);
    } catch (_error) {
      setDownloadErrors((current) => setRecordFlag(current, invoiceUuid, true));
    } finally {
      delete downloadRequests.current[invoiceUuid];
      setDownloading((current) => setRecordFlag(current, invoiceUuid, false));
    }
  };

  return (
    <Box component="section" aria-labelledby="invoices-heading" sx={{ color: "#000", mb: 5 }}>
      <Typography id="invoices-heading" variant="h4">
        Invoices
      </Typography>
      <Divider sx={{ mt: 1, mb: 3 }} />

      {hasError && (
        <Alert
          severity="error"
          sx={COMPACT_INVOICE_ALERT_SX}
          action={
            <Button color="inherit" size="small" onClick={() => setRetryKey((current) => current + 1)}>
              Retry
            </Button>
          }
        >
          <Typography component="span" sx={COMPACT_INVOICE_MESSAGE_SX}>
            Could not load invoices. Please try again.
          </Typography>
        </Alert>
      )}

      {loading && invoices.length === 0 && (
        <Box role="status" aria-label="Loading invoices" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress aria-hidden sx={{ color: "black" }} />
          <Typography>Loading invoices…</Typography>
        </Box>
      )}

      {!loading && !hasError && invoices.length === 0 && (
        <Typography>No invoices are available yet.</Typography>
      )}

      {invoices.length > 0 && (
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
          {invoices.map((invoice) => {
            const isDownloading = Boolean(downloading[invoice.invoice_uuid]);
            const hasDownloadError = Boolean(downloadErrors[invoice.invoice_uuid]);

            return (
              <Box
                component="li"
                key={invoice.invoice_uuid}
                sx={{
                  border: "1px solid black",
                  borderRadius: "8px",
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" },
                  alignItems: { xs: "stretch", md: "center" },
                  p: 2,
                  mb: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ overflowWrap: "anywhere" }}>
                    <b>{invoice.invoice_number}</b>
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(0, 0, 0, 0.68)",
                      display: "block",
                      fontSize: "0.625rem",
                      lineHeight: 1.2,
                      mb: 0.5,
                    }}
                  >
                    Issued {formatIssueDate(invoice.issue_date)}
                  </Typography>
                  <Typography sx={{ overflowWrap: "anywhere" }}>
                    {invoiceDescription(invoice)}
                  </Typography>
                </Box>

                <Box sx={{ minWidth: { md: 180 } }}>
                  <Button
                    fullWidth
                    aria-label={`Download invoice ${invoice.invoice_number}`}
                    disabled={!invoice.download_available || isDownloading}
                    onClick={() => handleDownload(invoice)}
                    startIcon={isDownloading ? <CircularProgress aria-hidden size={16} /> : <DownloadIcon />}
                    color="secondary"
                    variant="contained"
                    sx={{ background: "#fff" }}
                  >
                    {isDownloading ? "Downloading…" : "Download"}
                  </Button>
                  {!invoice.download_available && (
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 0.75, textAlign: { md: "center" }, ...COMPACT_INVOICE_MESSAGE_SX }}
                    >
                      Download unavailable
                    </Typography>
                  )}
                  {hasDownloadError && (
                    <Typography
                      role="alert"
                      variant="caption"
                      sx={{ color: "error.main", display: "block", mt: 0.75, ...COMPACT_INVOICE_MESSAGE_SX }}
                    >
                      {INVOICE_ERROR_MESSAGE}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
